import React from 'react';
import { ERROR } from './message';
import { getMessage } from './util';

/**
 * 文字種チェックのパターンなどを定義
 * @type     {object} 入力チェックに使用するCONST値
 * @property {object} PATERN_ALPHABET_NUM 文字種パターン：半角英数字
 * @property {object} PATERN_NUM 文字種パターン：半角数字
 * @property {object} PATERN_ALPHABET 文字種パターン：半角英字
 * @property {object} PATERN_PASSWORD 文字種パターン：半角英数記号
 * @property {object} PATERN_POSITIVE 文字種パターン：半角正数
 * @property {object} PATERN_SIGNED 文字種パターン：半角整数
 * @property {object} PATERN_DECIMAL 文字種パターン：半角小数
 * @property {object} PATERN_FULL_WIDTH 文字種パターン：全角
 * @property {object} PATERN_DATE 文字種パターン：日付
 * @property {object} PATERN_CSV 文字種パターン：CSV
 * @property {object} PATERN_ALPHABET_NUM_LEAST_ONE 文字種パターン：半角英数字最低1文字含む
 */
export const VALIDATOR_CONST = {
  PATERN_ALPHABET_NUM: {
    regex: /^[0-9a-zA-Z]*$/g,
    msg: '半角英数字',
  },
  PATERN_NUM: {
    regex: /^[0-9]*$/g,
    msg: '半角数字',
  },
  PATERN_ALPHABET: {
    regex: /^[a-zA-Z]*$/g,
    msg: '半角英字',
  },
  PATERN_PASSWORD: {
    regex: /^[0-9a-zA-Z`˜!@#$%^&*()_+\-={}[\]\\|:;"'<>,.?/]*$/g,
    msg: '半角英数記号',
  },
  PATERN_POSITIVE: {
    regex: /^([1-9][0-9]*|0)?$/g,
    msg: '半角正数',
  },
  PATERN_SIGNED: {
    regex: /^(-?[1-9][0-9]*|0)?$/g,
    msg: '半角整数(マイナス含む)',
  },
  PATERN_DECIMAL: {
    regex: /^-?([1-9][0-9]*|0)(\.[0-9]{1,2})?$/g,
    msg: '半角小数(マイナス含む、小数点以下２桁まで)',
  },
  PATERN_FULL_WIDTH: {
    regex: /^(?!.*[\x20-\x7E\uFF61-\uFF9F]).*$/g,
    msg: '全角',
  },
  PATERN_DATE: {
    regex: /^\d{4}\/\d{2}\/\d{2}$/,
    msg: '日付',
  },
  PATERN_CSV: {
    regex: /.+.(csv|CSV)/g,
    msg: 'CSV',
  },
  PATERN_TSV: {
    regex: /.+.(tsv|TSV)/g,
    msg: 'TSV',
  },
  PATERN_ALPHABET_NUM_LEAST_ONE: {
    regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[0-9a-zA-Z`˜!@#$%^&*()_+\-={}[\]\\|:;"'<>,.?/]{0,}$/g,
    msg: '英子文字と英大文字と数字最低1文字ずつ含んだ形式',
  },
  PATERN_YEAR: {
    regex: /^\d{4}$/,
    msg: '年は半角正数4桁',
  },
  PATERN_MONTH: {
    regex: /^(0?[1-9]|1[0-2])$/,
    msg: '月は1～12の半角正数',
  },
};

/**
 * エラーチェック時に利用する文言を定義
 * @type     {object} 文言のCONST値
 * @property {string} LENGTH 文字数(固定)
 * @property {string} LENGTH_UPPER 文字数(以上)
 * @property {string} LENGTH_LOWER 文字数(以下)
 * @property {string} LENGTH_RANGE 文字数(範囲)
 * @property {string} NUMBER_RANGE 数値(範囲)
 */
export const MSG_CONST = {
  LENGTH: '{0}文字',
  LENGTH_UPPER: '{0}文字以上',
  LENGTH_LOWER: '{0}文字以下',
  LENGTH_RANGE: '{0}～{1}文字',
  NUMBER_RANGE: '{0}～{1}の範囲',
};

/**
 * 共通入力チェック
 * @function checkRequired 必須チェック
 * @function checkLength 文字数チェック
 * @function checkMinLength 最小文字数チェック
 * @function checkMaxLength 最大文字数チェック
 * @function checkChar 文字種チェック
 * @function checkLengthRange 桁数の入力値範囲チェック
 * @function checkNumberRange 数値入力値の範囲チェック
 * @function checkIsDate 日付の妥当性チェック
 */
class validator {
  /**
   * 必須チェック
   * @param   {string}        name  入力項目名
   * @param   {string}        value 入力値
   * @param   {JSX.Element[]} msgs  メッセージ配列
   */
  checkRequired = (name, value, msgs) => {
    if (!value) {
      msgs.push(<div key={name}>{getMessage(ERROR.E08001, name)}</div>);
      return false;
    }
    return true;
  };

  /**
   * 文字数チェック
   * @param   {string}   value 入力値
   * @param   {number}   len   桁数
   * @param   {string[]} msgs  メッセージ配列
   */
  checkLength = (value, len, msgs) => {
    if (value && value.length !== len) { msgs.push(getMessage(MSG_CONST.LENGTH, len.toString())); }
  };

  /**
   * 最小文字数チェック
   * @param   {string}   value  入力値
   * @param   {number}   minlen 最小桁数
   * @param   {string[]} msgs   メッセージ配列
   */
  checkMinLength = (value, minlen, msgs) => {
    if (value && value.length < minlen) {
      msgs.push(getMessage(MSG_CONST.LENGTH_UPPER, minlen.toString()));
    }
  };

  /**
   * 最大文字数チェック
   * @param   {string}   value  入力値
   * @param   {number}   maxlen 最大桁数
   * @param   {string[]} msgs   メッセージ配列
   */
  checkMaxLength = (value, maxlen, msgs) => {
    if (value && value.length > maxlen) {
      msgs.push(getMessage(MSG_CONST.LENGTH_LOWER, maxlen.toString()));
    }
  };

  /**
   * 文字種チェック
   * @param   {string}   value  入力値
   * @param   {object}   patern 文字種パターン
   * @param   {string[]} msgs   メッセージ配列
   */
  checkChar = (value, patern, msgs) => {
    if (value && !value.match(patern.regex)) msgs.push(patern.msg);
  };

  /**
   * 桁数の入力値範囲チェック
   * @param   {string}   value  入力値
   * @param   {number}   minlen 最小桁数
   * @param   {number}   maxlen 最大桁数
   * @param   {string[]} msgs   メッセージ配列
   */
  checkLengthRange = (value, minlen, maxlen, msgs) => {
    if (value && (value.length < minlen || value.length > maxlen)) {
      msgs.push(getMessage(MSG_CONST.LENGTH_RANGE, minlen.toString(), maxlen.toString()));
    }
  }

  /**
   * 数値入力値の範囲チェック
   * @param   {string}   value  入力値
   * @param   {number}   minnum 最小値
   * @param   {number}   maxnum 最大値
   * @param   {string[]} msgs   メッセージ配列
   */
  checkNumberRange = (value, minnum, maxnum, msgs) => {
    // 入力値が数値以外の場合、チェックを行わない
    if (value && value.match(VALIDATOR_CONST.PATERN_SIGNED.regex)) {
      if (Number(value) < minnum || Number(value) > maxnum) {
        msgs.push(getMessage(MSG_CONST.NUMBER_RANGE, minnum.toString(), maxnum.toString()));
      }
    }
  }

  /**
   * 日付の妥当性チェック
   * @param   {string}        name  入力項目名
   * @param   {string}        value 入力値
   * @param   {JSX.Element[]} msgs  メッセージ配列
   */
  checkIsDate = (name, value, msgs) => {
    if (!value) {
      return;
    }
    if (!value.match(VALIDATOR_CONST.PATERN_DATE.regex)) {
      msgs.push(<div key={name}>{getMessage(ERROR.E08013, name)}</div>);
      return;
    }
    const d = new Date(value);
    if (d.getFullYear() !== Number(value.split('/')[0])
      || d.getMonth() !== Number(value.split('/')[1] - 1)
      || d.getDate() !== Number(value.split('/')[2])
    ) {
      msgs.push(<div key={name}>{getMessage(ERROR.E08012, name)}</div>);
    }
  }

  /**
   * ファイル形式チェック
   * @param   {string}        name         入力項目名
   * @param   {string}        file         ファイル
   * @param   {string}        maxFileSize  ファイルサイズ最大値
   * @param   {string}        patern       文字種パターン
   * @param   {string}        maxFileName  ファイル名最大値
   * @param   {JSX.Element[]} msgs         メッセージ配列
   * @param   {string}        key          キー
   */
  checkFileStatus = (name, file, maxFileSize, patern, maxFileName, msgs, key) => {
    // 必須チェック
    if (!file) {
      msgs.push(<div key={key}>{getMessage(ERROR.E08014, name)}</div>);
      return;
    }
    // 空、存在チェック
    if (file.size === 0) {
      msgs.push(<div key={key}>{getMessage(ERROR.E08018, name)}</div>);
      return;
    }
    // ファイルサイズチェック
    if (file.size > maxFileSize) {
      const kb = 1024;
      const mb = kb ** 2;
      let target = null;
      let unit = 'byte';
      if (maxFileSize >= mb) {
        target = mb;
        unit = 'MB';
      } else if (maxFileSize >= kb) {
        target = kb;
        unit = 'KB';
      }
      const dispSize = target !== null
        ? Math.floor((maxFileSize / target) * 100) / 100
        : maxFileSize;
      msgs.push(<div key={key}>{getMessage(ERROR.E08015, dispSize, unit, name)}</div>);
      return;
    }
    // 拡張子チェック
    if (!file.name.match(patern.regex)) {
      msgs.push(<div key={key}>{getMessage(ERROR.E08016, patern.msg, name)}</div>);
      return;
    }
    // 拡張子を除いたファイル名
    const fileName = file.name.substr(0, file.name.lastIndexOf('.'));
    // ファイル名文字数チェック
    if (fileName.length > maxFileName) {
      msgs.push(<div key={key}>{getMessage(ERROR.E08017, maxFileName, name)}</div>);
    }
  }

  /**
   * 同値チェック
   * @param   {string}        item1  入力項目名1
   * @param   {string}        item2  入力項目名2
   * @param   {string}        value1 入力値1
   * @param   {string}        value2 入力値2
   * @param   {JSX.Element[]} msgs   メッセージ配列
   */
  checkSame = (item1, item2, value1, value2, msgs) => {
    if (value1 === value2) {
      msgs.push(getMessage(ERROR.E08022, item1, item2));
    }
  }

  /**
   * 相違チェック
   * @param   {string}        item1  入力項目名1
   * @param   {string}        item2  入力項目名2
   * @param   {string}        value1 入力値1
   * @param   {string}        value2 入力値2
   * @param   {JSX.Element[]} msgs   メッセージ配列
   */
  checkDifference = (item1, item2, value1, value2, msgs) => {
    if (value1 !== value2) {
      msgs.push(getMessage(ERROR.E08023, item1, item2));
    }
  }

  /**
   * 下限チェック
   * @param   {string}        item  入力項目名
   * @param   {number}        min   最小値
   * @param   {string[]}      input 入力値
   * @param   {JSX.Element[]} msgs  メッセージ配列
   */
  checkLeast = (item, min, input, msgs) => {
    if (input === null) {
      msgs.push(getMessage(ERROR.E08024, item, min));
    }
    if (input && input.length < min) {
      msgs.push(getMessage(ERROR.E08024, item, min));
    }
  }
}

export default new validator();
